import { StackActions, useNavigation } from '@react-navigation/native';
import { createStackNavigator } from '@react-navigation/stack';
import { Elements } from '@stripe/react-stripe-js';
import { StripeElementsOptions } from '@stripe/stripe-js';
import { Formik } from 'formik';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Platform } from 'react-native';
import { InferType } from 'yup';

import { SkillSelect } from '@components/FormModals/SkillSelect';
import { TeamsModal } from '@components/FormModals/TeamsModal';
import { WebModal } from '@components/Modals/WebModal';
import { StripeUnavailable } from '@components/Payments/StripeUnavailable';
import { PaymentElementFailureContext } from '@components/Payments/WatchedPaymentElement';
import { useWebDrawer } from '@components/Web/Drawer/WebDrawerContext';
import { LocalFile, TeamRole, User } from '@graphql/generated';
import { useListTeamsFromQuery } from '@hooks/useListTeamsFromQuery';
import useMe from '@hooks/useMe';
import { useStripeLoader } from '@hooks/useStripeLoader';
import { useTeamFromQuery } from '@hooks/useTeamFromQuery';
import { AddTeamMembersStack } from '@navigation/addMembers/add-team-members-stack';
import { createSubscriptionSchema } from '@schemas/createSubscriptionSchema';
import { CreateSubscription } from '@screens/Payments/CreateSubscription';
import { EditSubscription } from '@screens/Payments/EditSubscription';
import { DeferredTeamMember } from '@utils/teams/deferredTeamSubscription';

const stripePublishableKey =
  process.env.AASA_MODE === 'production'
    ? process.env.PROD_STRIPE_PUBLIC_KEY || 'not set'
    : process.env.STRIPE_PUBLIC_KEY ||
      'pk_test_51NuWiYKiMQhaWaB8lZReWb2agfurIZEqvDIqy8SXewKfgNc5l649X30gHZH3oylt0u4paLws8PWfv0ShI80Lvukw00eykmS5a8';

export type CreateSubscriptionStackParamsList = {
  home: undefined;
  'create-subscriptionRightPanel': undefined;
  'add-team-members': undefined;
  'create-team': undefined;
  'choose-team': undefined;
  'skill-select': undefined;
};

const Stack = createStackNavigator<CreateSubscriptionStackParamsList>();

export type InferredFormValues = InferType<typeof createSubscriptionSchema>;
type UserWithRole = User & { role: TeamRole };
export type FormValues = Omit<
  InferredFormValues,
  'logo' | 'skillIds' | 'users'
> & {
  logo: LocalFile | null;
  skillIds: string[];
  users: UserWithRole[];
  // Deferred e-mail invitations (TA-10009): sent as
  // teamAttributes.teamMembers when the subscription creates the team
  teamMembers?: DeferredTeamMember[];
};
const CreateSubscriptionStack: React.FC<{
  externalNavigate?: any;
  params: any;
}> = ({ externalNavigate, params }) => {
  // TODO complete modal for confirmation
  // TODO add paywall prompts in all appropriate screens
  const { t } = useTranslation();
  const { me } = useMe();
  const { teamUUId, isPersonal, isPlanPlus, screenName, teamValues } = params;

  const { upgradeCTA, setUpgradeTeamPlanOpen } = useWebDrawer();
  // TA-10957: a blocked js.stripe.com used to leave Elements holding a
  // rejected promise with no way out; show a retry-capable fallback instead.
  const { stripe, loadFailed, retrying, retry, reportLoadFailure } =
    useStripeLoader(stripePublishableKey);

  const { team } = useTeamFromQuery({
    teamUUId,
  });

  const [teamId, setTeamId] = useState<string>('');

  useEffect(() => {
    team?.id && setTeamId(team?.id);
  }, [team]);

  const users = () => {
    if (!me) {
      return [];
    }
    const owner = {
      id: me.id,
      avatar: me.avatar,
      name: me.name,
      firstName: me.firstName,
      lastName: me.lastName,
      role: TeamRole.Owner,
    };
    return [owner];
  };

  const teamUsers = team
    ? team.users.map((u) => {
        const a = {
          ...u,
          role:
            (u.teamRoles?.length ?? 0) > 0 ? u.teamRoles![0] : TeamRole.Member,
        };
        return a;
      })
    : users();
  const initialValues: FormValues = {
    users:
      teamValues?.users && teamValues?.users.length > 0
        ? [
            ...teamUsers,
            ...teamValues.users.filter(
              (u: User) => !teamUsers.some((u1) => u1.id === u.id)
            ),
          ]
        : teamUsers,
    teamId: Platform.OS == 'web' ? teamId || null : null,
    teamUUId: teamUUId,
    plan: team ? team.subscriptionDetails?.stripeProductId : null,
    planName: team ? team.subscriptionDetails?.subscriptionPlanName : null,
    billingCycle: teamValues?.billingCycle ?? 'year',
    paymentComplete: false,
    priceId: null,
    price: 0,
    location: teamValues?.location ?? '',
    name: team ? team.name : (teamValues?.name ?? ''),
    skillIds: team
      ? team.skills.map((s) => s.id)
      : (teamValues?.skillIds ?? []),
    logo: teamValues?.logo ?? null,
    teamMembers: team ? [] : (teamValues?.teamMembers ?? []),
  };
  // Warms the Apollo cache for `listTeams` — EditSubscription reads teams via
  // `useListTeamsQuery({ fetchPolicy: 'cache-only' })` and depends on this
  // stack having already fetched them. Result intentionally unused here.
  useListTeamsFromQuery({});
  const navigator = useNavigation();

  if (loadFailed) {
    return (
      <StripeUnavailable
        onRetry={retry}
        retrying={retrying}
        onClose={() => setUpgradeTeamPlanOpen(false)}
      />
    );
  }

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={createSubscriptionSchema}
      validateOnMount
      onSubmit={() => {}}>
      {({ values, setFieldValue, touched, setTouched }) => {
        const elementOptions: StripeElementsOptions = {
          paymentMethodCreation: 'manual',
          mode: 'subscription',
          amount: values.price || 0,
          currency: 'usd',
          appearance: {
            /*...*/
          },
        };

        return (
          <PaymentElementFailureContext.Provider value={reportLoadFailure}>
            <Elements stripe={stripe} options={elementOptions}>
              <Stack.Navigator
                screenOptions={{ headerShown: true }}
                initialRouteName={
                  screenName === 'create-subscriptionPanel'
                    ? 'create-subscriptionRightPanel'
                    : 'home'
                }>
                <Stack.Screen
                  name='home'
                  component={CreateSubscription}
                  options={{
                    headerShown: false,
                  }}
                  initialParams={{ isPersonal, teamUUId, isPlanPlus }}
                />
                <Stack.Screen
                  name='create-subscriptionRightPanel'
                  children={() => {
                    return team?.subscriptionDetails &&
                      !team?.subscriptionDetails?.isSubscriptionCanceled &&
                      !upgradeCTA ? (
                      <EditSubscription upgradeCTA={upgradeCTA} />
                    ) : (
                      <CreateSubscription externalNavigate={externalNavigate} />
                    );
                  }}
                  options={{
                    headerShown: false,
                  }}
                  initialParams={{ isPersonal, teamUUId, isPlanPlus }}
                />
                <Stack.Group
                  screenOptions={{
                    headerShown: false,
                    presentation: 'transparentModal',
                    cardShadowEnabled: Platform.OS === 'web',
                    cardStyleInterpolator: ({ current: { progress } }) => ({
                      containerStyle: {
                        opacity: progress.interpolate({
                          inputRange: [0, 1],
                          outputRange: [0, 1],
                          extrapolate: 'clamp',
                        }),
                      },
                      overlayStyle: {
                        opacity: progress.interpolate({
                          inputRange: [0, 1],
                          outputRange: [0, 0.6],
                          extrapolate: 'clamp',
                        }),
                      },
                    }),
                  }}>
                  <Stack.Screen
                    name='add-team-members'
                    children={() => (
                      <WebModal
                        accessibilityLabel='Team - Add Members'
                        onClose={() => {
                          navigator.dispatch(StackActions.pop());
                        }}
                        visible={true}
                        width={504}
                        height={514}>
                        <AddTeamMembersStack
                          isFromSubscription={true}
                          initialUsers={values.users}
                          onAddMembers={(users) => {
                            setFieldValue('users', users);
                            setTouched({ ...touched, ['users']: true });
                          }}
                          onClose={() => {
                            navigator.dispatch(StackActions.pop());
                          }}
                        />
                      </WebModal>
                    )}
                    options={{ title: 'Add Paid Members' }}></Stack.Screen>

                  <Stack.Screen name='choose-team' options={{ title: 'Teams' }}>
                    {() => (
                      // TODO need to pass something as a way to filter teams out that already have entitlements
                      <TeamsModal
                        title='Team Setup'
                        buttonLabel={t('shared:next')}
                      />
                    )}
                  </Stack.Screen>
                  <Stack.Screen
                    name='skill-select'
                    options={{ title: 'Skills' }}>
                    {() => (
                      <SkillSelect
                        onClose={() => {
                          navigator.dispatch(
                            StackActions.replace('choose-team')
                          );
                        }}
                        onNext={() => {
                          navigator.dispatch(
                            StackActions.replace('choose-team')
                          );
                        }}
                      />
                    )}
                  </Stack.Screen>
                </Stack.Group>
              </Stack.Navigator>
            </Elements>
          </PaymentElementFailureContext.Provider>
        );
      }}
    </Formik>
  );
};

export default CreateSubscriptionStack;
